import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Settings } from 'lucide-react';

const SettingsTab = ({ authAxios }) => {
  const [analyticsSettings, setAnalyticsSettings] = useState({ google_analytics_id: '', plausible_domain: '' });
  const [seoSettings, setSeoSettings] = useState({ seo_title: '', seo_description: '', seo_keywords: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await authAxios.get('/admin/settings');
        const data = response.data;
        setAnalyticsSettings({
          google_analytics_id: data.google_analytics_id || '',
          plausible_domain: data.plausible_domain || '',
        });
        setSeoSettings({
          seo_title: data.seo_title || '',
          seo_description: data.seo_description || '',
          seo_keywords: data.seo_keywords || '',
        });
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetch();
  }, [authAxios]);

  const handleSaveAnalytics = async () => {
    setSaving(true);
    try {
      const currentSettings = await authAxios.get('/admin/settings');
      await authAxios.put('/admin/settings', {
        ...currentSettings.data,
        google_analytics_id: analyticsSettings.google_analytics_id,
        plausible_domain: analyticsSettings.plausible_domain,
      });
      toast.success('Analytics settings saved! Refresh page to activate.');
    } catch (error) {
      toast.error('Failed to save analytics settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeo = async () => {
    setSaving(true);
    try {
      const currentSettings = await authAxios.get('/admin/settings');
      await authAxios.put('/admin/settings', {
        ...currentSettings.data,
        seo_title: seoSettings.seo_title,
        seo_description: seoSettings.seo_description,
        seo_keywords: seoSettings.seo_keywords,
      });
      toast.success('SEO settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-tab">
      {/* Analytics Settings */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-2">Analytics Tracking</h2>
        <p className="text-white/50 text-xs mb-6">Configure Google Analytics 4 and Plausible to track visitor engagement</p>
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Google Analytics 4 Measurement ID</label>
            <input type="text" placeholder="G-XXXXXXXXXX" value={analyticsSettings.google_analytics_id} onChange={(e) => setAnalyticsSettings({...analyticsSettings, google_analytics_id: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            <p className="text-white/30 text-xs mt-1">Find this in Google Analytics &gt; Admin &gt; Data Streams</p>
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Plausible Domain</label>
            <input type="text" placeholder="aureate-resident.com" value={analyticsSettings.plausible_domain} onChange={(e) => setAnalyticsSettings({...analyticsSettings, plausible_domain: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
          </div>
          <button onClick={handleSaveAnalytics} disabled={saving} data-testid="save-analytics" className="px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Analytics Settings'}
          </button>
        </div>
      </div>

      {/* SEO Settings */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-2">SEO Settings</h2>
        <p className="text-white/50 text-xs mb-6">Optimize your site for search engines</p>
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Page Title</label>
            <input type="text" placeholder="Aureate Residences | Professional Property Management" value={seoSettings.seo_title} onChange={(e) => setSeoSettings({...seoSettings, seo_title: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            <p className="text-white/30 text-xs mt-1">Recommended: 50-60 characters</p>
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Meta Description</label>
            <textarea placeholder="Professional property management services..." value={seoSettings.seo_description} onChange={(e) => setSeoSettings({...seoSettings, seo_description: e.target.value})} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
            <p className="text-white/30 text-xs mt-1">Recommended: 150-160 characters ({seoSettings.seo_description?.length || 0} chars)</p>
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Keywords</label>
            <textarea placeholder="accommodation solutions Edinburgh, property management Scotland, landlord solutions..." value={seoSettings.seo_keywords} onChange={(e) => setSeoSettings({...seoSettings, seo_keywords: e.target.value})} rows={2} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
          </div>
          <button onClick={handleSaveSeo} disabled={saving} data-testid="save-seo" className="px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save SEO Settings'}
          </button>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6">Notification Settings</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-white/5">
            <div>
              <p className="font-mono text-sm">Email Notifications</p>
              <p className="text-white/50 text-xs mt-1">Receive emails for new submissions</p>
            </div>
            <div className="w-12 h-6 bg-[#D4AF37] rounded-full relative cursor-pointer">
              <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
            </div>
          </div>
          <div className="p-4 bg-[#0A0A0A] border border-white/5">
            <p className="font-mono text-sm mb-2">Notification Email</p>
            <input type="email" value="info@aureate-resident.com" readOnly className="w-full bg-transparent border border-white/10 px-4 py-2 font-mono text-sm text-white/70" />
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6">Security</h2>
        <div className="p-4 bg-[#0A0A0A] border border-white/5">
          <p className="font-mono text-sm">Admin PIN</p>
          <p className="text-white/50 text-xs mt-1">Contact developer to change PIN</p>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
